import { inspectionClient } from '@/config/api';

import {
  AiAnalysisStatus,
  type AiAnalysisJob,
  type AiAnalysisResult,
  type AutomaticReportResponse,
} from './types/aiAnalysis.types';

export { AiAnalysisStatus };

export type {
  AiAnalysisJob,
  AiAnalysisResult,
  AutomaticReportResponse,
  AutomaticReportData,
  AutomaticReportHeader,
  AutomaticReportDadosPedido,
  AutomaticReportDadosImovel,
  AutomaticReportResumoAvaliacao,
  AutomaticReportAmostra,
  AutomaticReportCalculoItem,
  AutomaticReportCalculos,
  AutomaticReportHomogeneizacao,
  AutomaticReportSaneamento,
  AutomaticReportAvaliacaoFinal,
  AutomaticReportMetodologia,
} from './types/aiAnalysis.types';

export const aiAnalysisService = {
  /**
   * Obtém o job de análise IA pelo ID do item de inspeção
   */
  async getAiAnalysis(inspectionItemId: string): Promise<AiAnalysisJob | null> {
    try {
      const response = await inspectionClient.get<AiAnalysisJob>(
        `/api/ai-analysis/${inspectionItemId}`
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Alias para getAiAnalysis - mantido para compatibilidade
   */
  async getByInspectionItemIdAsync(inspectionItemId: string): Promise<AiAnalysisJob | null> {
    return this.getAiAnalysis(inspectionItemId);
  },

  /**
   * Dispara a análise de IA real usando o scraping job concluído
   * Este método substitui o mock e chama a IA real
   */
  async triggerAiAnalysis(scrapingJobId: string): Promise<AiAnalysisJob> {
    const response = await inspectionClient.post<AiAnalysisJob>(
      `/api/ai-analysis/scraping-jobs/${scrapingJobId}/trigger`
    );
    return response.data;
  },

  async getAutomaticReportData(
    inspectionItemId: string
  ): Promise<AutomaticReportResponse> {
    const response = await inspectionClient.get<AutomaticReportResponse>(
      `/api/ai-analysis/${inspectionItemId}/report-data`
    );
    return response.data;
  },

  /**
   * Cancela o processamento automático (scraping/IA) em andamento.
   * Marca os jobs ativos como Cancelled e leva o item para AnaliseIAFalhou,
   * permitindo que o banco preencha o laudo manualmente.
   */
  async cancelAutomaticProcessing(inspectionItemId: string): Promise<void> {
    await inspectionClient.post(
      `/api/ai-analysis/${inspectionItemId}/cancel-automatic`
    );
  },

  /**
   * Faz parse do resultJson em objeto tipado
   */
  parseResult(job: AiAnalysisJob): AiAnalysisResult | null {
    if (!job.resultJson) return null;
    try {
      return JSON.parse(job.resultJson) as AiAnalysisResult;
    } catch {
      return null;
    }
  },

  isProcessing(job: AiAnalysisJob | null): boolean {
    if (!job) return false;
    return (
      job.status === AiAnalysisStatus.Pending ||
      job.status === AiAnalysisStatus.Processing
    );
  },

  isCompleted(job: AiAnalysisJob | null): boolean {
    return !!job && job.status === AiAnalysisStatus.Completed;
  },

  isFailed(job: AiAnalysisJob | null): boolean {
    return !!job && job.status === AiAnalysisStatus.Failed;
  },
};

export default aiAnalysisService;
